/**
 * チャット機能のカスタムフック
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage, AgeGroup, AttachedFileInfo, ChatLimitInfo } from '../types';
import {
  sendMessage,
  getSessionMessages,
  getCachedUsageLimits,
  prefetchUsageLimits,
  updateCachedUsageLimits,
} from '../services/api';
import { generateUUID, getCurrentTimestamp } from '@/utils';
import { transformChatMessages } from '@/utils/transformers';

export function useChat(sessionId: string, ageGroup: AgeGroup) {
  const { i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  // useRefで同期的にloadingMessageIdを管理（useStateは非同期更新のため）
  const loadingMessageIdRef = useRef<string | null>(null);
  const [chatUsage, setChatUsage] = useState<ChatLimitInfo>(
    () => getCachedUsageLimits()?.chat ?? { remaining: null, limit: null, reset_at: null }
  );

  // 月次使用状況を読み込む（キャッシュ済みなら初期stateに設定済みのためスキップ）
  useEffect(() => {
    if (getCachedUsageLimits()) return;

    const loadUsageLimits = async () => {
      try {
        const usage = await prefetchUsageLimits();
        setChatUsage(usage.chat);
      } catch {
        // Usage limits load failed - will use default values
      }
    };

    loadUsageLimits();
  }, []);

  const completeStreaming = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId && m.isStreaming ? { ...m, isStreaming: false } : m))
    );
  }, []);

  // sessionIdが変更されたときにメッセージを読み込む
  useEffect(() => {
    const loadMessages = async () => {
      if (!sessionId) {
        setMessages([]);
        setIsLoadingSession(false);
        return;
      }

      setIsLoadingSession(true);
      try {
        const sessionMessages = await getSessionMessages(sessionId);

        // バックエンドから取得したメッセージを変換（transformers.ts で一元管理）
        const convertedMessages = transformChatMessages(sessionMessages);

        setMessages(convertedMessages);
      } catch {
        setMessages([]);
      } finally {
        setIsLoadingSession(false);
      }
    };

    loadMessages();
  }, [sessionId]);

  // ファイルアップロード開始時にユーザーメッセージとローディング表示を追加
  const startUploading = useCallback(
    (userText: string, attachedFiles?: AttachedFileInfo[]) => {
      if (isLoading) return;

      setIsLoading(true);

      // ローディング表示を追加
      const loadingId = generateUUID();
      loadingMessageIdRef.current = loadingId;

      // ユーザーメッセージを作成
      const userMessage: ChatMessage = {
        id: generateUUID(),
        session_id: sessionId,
        user_input: userText,
        ai_response: '',
        timestamp: getCurrentTimestamp(),
        age_group: ageGroup,
        isUser: true,
        attachedFiles,
      };

      // ユーザーメッセージとローディング表示を同時に追加
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          id: loadingId,
          session_id: sessionId,
          user_input: '',
          ai_response: '',
          timestamp: getCurrentTimestamp(),
          age_group: ageGroup,
          isUser: false,
          isLoading: true,
        },
      ]);
    },
    [sessionId, ageGroup, isLoading]
  );

  const addMessage = useCallback(
    async (text: string, attachedFileIds?: string[]) => {
      // 既にローディング表示がある場合（ファイルアップロード開始時）
      const existingLoadingId = loadingMessageIdRef.current;

      // 通常のメッセージ送信時はisLoadingチェック、ファイルアップロード時はスキップ
      if (!existingLoadingId) {
        if ((!text.trim() && !attachedFileIds?.length) || isLoading) return;
      }

      let loadingId: string;

      if (existingLoadingId) {
        // ファイルアップロード時：既にユーザーメッセージとローディングが追加済み
        loadingId = existingLoadingId;
        loadingMessageIdRef.current = null;
      } else {
        // 通常のメッセージ送信時
        setIsLoading(true);
        loadingId = generateUUID();

        // ユーザーメッセージを作成
        const userMessage: ChatMessage = {
          id: generateUUID(),
          session_id: sessionId,
          user_input: text,
          ai_response: '',
          timestamp: getCurrentTimestamp(),
          age_group: ageGroup,
          isUser: true,
        };

        // ユーザーメッセージとローディング表示を追加
        setMessages((prev) => [
          ...prev,
          userMessage,
          {
            id: loadingId,
            session_id: sessionId,
            user_input: '',
            ai_response: '',
            timestamp: getCurrentTimestamp(),
            age_group: ageGroup,
            isUser: false,
            isLoading: true,
          },
        ]);
      }

      try {
        // API呼び出し
        const response = await sendMessage({
          message: text,
          session_id: sessionId,
          age_group: ageGroup,
          attached_file_ids: attachedFileIds,
          language: i18n.language,
        });

        // 月次使用状況を更新
        if (response.chat_limit) {
          const chatLimit: ChatLimitInfo = {
            remaining: response.chat_limit.remaining ?? null,
            limit: response.chat_limit.limit ?? null,
            reset_at: response.chat_limit.reset_at ?? null,
          };
          setChatUsage(chatLimit);
          updateCachedUsageLimits({ chat: chatLimit });
        }

        // ローディングを削除してAI応答を追加
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== loadingId);
          const hasEvent = !!response.event;
          return [
            ...filtered,
            {
              id: response.message_id,
              session_id: sessionId,
              user_input: text,
              ai_response: response.message ?? '',
              timestamp: getCurrentTimestamp(),
              age_group: ageGroup,
              isUser: false,
              isError: response.filtered,
              isStreaming: !response.filtered && !hasEvent,
              generatedImage: response.generated_image,
              sources: response.sources,
              event: response.event,
            },
          ];
        });
      } catch {
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== loadingId);
          return [
            ...filtered,
            {
              id: generateUUID(),
              session_id: sessionId,
              user_input: text,
              ai_response: '',
              timestamp: getCurrentTimestamp(),
              age_group: ageGroup,
              isUser: false,
              isError: true,
              event: {
                type: 'generation_error',
                params: {},
              },
            },
          ];
        });
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, ageGroup, isLoading, i18n.language]
  );

  return {
    messages,
    addMessage,
    isLoading,
    isLoadingSession,
    chatUsage,
    startUploading,
    completeStreaming,
  };
}
